import React, { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { t } from 'ttag';

import { Nullable } from '../../../types/general';
import { Verify2FAFormFields } from './types';

import Alert from '../../ui/Alert/Alert';
import Button from '../../ui/button/button';
import Checkbox from '../../ui/checkbox/checkbox';
import Icon from '../../ui/icon/icon';
import OutlinedInput from '../../ui/input/OutlinedInput/OutlinedInput';
import PseudoLink from '../../ui/pseudolink/pseudolink';

import { redirect } from '../../../lib/navigation';
import { useInterval } from '../../../lib/reactHelpers/hooks';
import { getLocationQuery } from '../../../lib/url';
import eventTracking from '../../../services/analytics/eventTracking';
import auth from '../../../services/auth';
import { TOO_MANY_REQUEST_ERROR_CODE } from '../../AuthCheckEmail/constants';
import FlexBox from '../../FlexBox/FlexBox';
import PageTitle from '../../pageTitle/pageTitle';
import ResendCodeNotification from './components/ResendCodeNotification';
import { verify2FAFormResolver } from './validation';

import LockIcon from './images/lock.svg';

import partnersLogo from '../../../images/auth/partners-mobile.png';

import css from './NewVerify2FA.css';

/**
 * @see https://web.dev/articles/sms-otp-form - SMS OTP form best practices
 */
const NewVerify2FA: FC = () => {
    const [phoneNumber, setPhoneNumber] = useState('+************');
    const [resendCodeTimer, setResendCodeTimer] = useState<Nullable<number>>(
        null,
    );
    const [commonErrorMessage, setCommonErrorMessage] = useState<string>('');

    const history = useHistory();
    const searchQuery = getLocationQuery(history.location);

    const { control, handleSubmit, formState } = useForm<Verify2FAFormFields>({
        resolver: verify2FAFormResolver,
        defaultValues: {
            code: '',
            trusted: false,
        },
    });

    useInterval(
        () => {
            if (!resendCodeTimer) {
                return;
            }
            setResendCodeTimer(resendCodeTimer - 1);
        },
        resendCodeTimer ? 1000 : null,
    );

    useEffect(() => {
        auth.getHiddenPhone().then((data) => setPhoneNumber(data.phone));
    }, []);

    const onSubmit: SubmitHandler<Verify2FAFormFields> = async ({
        code,
        trusted,
    }) => {
        try {
            const nextUrl = await auth.verify2FACode(code, trusted);
            eventTracking.sendEvent('2fa_verify', 'success', null, () =>
                redirect(searchQuery?.redirect || nextUrl),
            );
        } catch (error) {
            const errorMessage = error.reason
                ? error.reason
                : t`Йой! Щось пішло не так.`;
            setCommonErrorMessage(errorMessage);
        }
    };

    const handleResendCode = async () => {
        try {
            await auth.resend2FACode();
            eventTracking.sendEvent('2fa_verify', 'resend', 'ok');
        } catch (error) {
            if (
                error.code === TOO_MANY_REQUEST_ERROR_CODE &&
                error.details?.lock_time
            ) {
                setResendCodeTimer(error.details?.lock_time);
                return;
            }
            eventTracking.sendEvent('2fa_verify', 'resend', error.message);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <PageTitle>{t`Двофакторна аутентифікація`}</PageTitle>
            <FlexBox className={css.container} justify="center" gap={0}>
                <div className={css.content}>
                    <img
                        src={partnersLogo}
                        className={css.partnersLogos}
                        alt=""
                    />
                    <FlexBox
                        className={css.lockIconContainer}
                        align="center"
                        justify="center"
                    >
                        <Icon className={css.lockIcon} glyph={LockIcon} />
                    </FlexBox>
                    <div className={css.titleContainer}>
                        <h1 className={css.title}>
                            {t`Двофакторна аутентифікація`}
                        </h1>
                        <h4 className={css.description}>
                            {t`Вкажіть код, який було вислано у Viber/SMS на ваш номер`}{' '}
                            {phoneNumber}
                        </h4>
                    </div>
                    <FlexBox
                        className={css.codeContainer}
                        direction="column"
                        gap={11}
                    >
                        <Controller
                            control={control}
                            name="code"
                            render={({ field, fieldState }) => (
                                <OutlinedInput
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    required
                                    label={t`Введіть код`}
                                    value={field.value}
                                    onChange={(event) =>
                                        field.onChange(
                                            event.target.value.trim(),
                                        )
                                    }
                                    autoFocus
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                        <div className={css.resendCodeLinkContainer}>
                            <PseudoLink
                                className={css.resendCodeLink}
                                onClick={handleResendCode}
                                disabled={!!resendCodeTimer}
                            >
                                {t`Надіслати повторно`}
                            </PseudoLink>
                        </div>
                    </FlexBox>
                    {!!resendCodeTimer && (
                        <ResendCodeNotification timer={resendCodeTimer} />
                    )}
                    <Controller
                        control={control}
                        name="trusted"
                        render={({ field }) => (
                            <Checkbox
                                checked={field.value}
                                onChange={field.onChange}
                                color="deepSkyBlue"
                                text={t`Це надійний пристрій. Підтверджувати себе за кодом SMS/Viber раз на місяць`}
                            />
                        )}
                    />
                    {commonErrorMessage && (
                        <Alert theme="error" hideIcon>
                            {commonErrorMessage}
                        </Alert>
                    )}
                    <Button
                        className={css.submitBtn}
                        type="submit"
                        theme="darkGray"
                        disabled={formState.isSubmitting}
                    >{t`Увійти`}</Button>
                </div>
            </FlexBox>
        </form>
    );
};

export default NewVerify2FA;
